import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';

import toast from 'react-hot-toast';

// ======================================================
// API BASE URL
// ======================================================

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://raastqr.vendingc.com/api';

// ======================================================
// CUSTOM ERROR TYPE
// ======================================================

interface ApiError {
  handled?: boolean;
  message: string;
  status?: number;
}

// ======================================================
// API CLIENT
// ======================================================

class ApiClient {
  private client: AxiosInstance;

  // Prevent duplicate toasts
  private offlineToastShown = false;
  private sessionToastShown = false;

  // Prevent multiple logout redirects
  private isRedirectingToLogin = false;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,

      timeout: 15000,

      headers: {
        'Content-Type': 'application/json',
      },

      withCredentials: false,
    });

    this.setupInternetListeners();
    this.setupInterceptors();
  }

  // ======================================================
  // INTERNET LISTENERS
  // ======================================================

  private setupInternetListeners(): void {
    window.addEventListener('offline', () => {

      if (!this.offlineToastShown) {

        this.offlineToastShown = true;

        toast.error(
          'No internet connection'
        );

        setTimeout(() => {
          this.offlineToastShown = false;
        }, 4000);
      }
    });

    window.addEventListener('online', () => {
      toast.success(
        'Internet connection restored'
      );
    });
  }

  // ======================================================
  // INTERCEPTORS
  // ======================================================

  private setupInterceptors(): void {

    // ==================================================
    // REQUEST INTERCEPTOR
    // ==================================================

    this.client.interceptors.request.use(

      (config) => {

        // ----------------------------------------------
        // BLOCK REQUEST IF OFFLINE
        // ----------------------------------------------

        if (!navigator.onLine) {

          return Promise.reject({
            handled: true,
            message:
              'No internet connection available.',
          });
        }

        // ----------------------------------------------
        // ADD AUTH TOKEN
        // ----------------------------------------------

        const token = localStorage.getItem('token');

        if (token && config.headers) {
          config.headers.Authorization =
            `Bearer ${token}`;
        }

        return config;
      },

      (error) => Promise.reject(error)
    );

    // ==================================================
    // RESPONSE INTERCEPTOR
    // ==================================================

    this.client.interceptors.response.use(

      (response) => response,

      (error: AxiosError<any>) => {

        console.error('API ERROR:', error);

        // ==================================================
        // NETWORK / INTERNET ERROR
        // IMPORTANT: MUST BE FIRST
        // ==================================================

        if (
          error.code === 'ERR_NETWORK' ||
          error.message === 'Network Error' ||
          !error.response
        ) {

          if (!this.offlineToastShown) {

            this.offlineToastShown = true;

            toast.error(
              'Unable to connect. Please check your internet connection.'
            );

            setTimeout(() => {
              this.offlineToastShown = false;
            }, 4000);
          }

          const customError: ApiError = {
            handled: true,
            message:
              'No internet connection.',
          };

          return Promise.reject(customError);
        }

        // ==================================================
        // REQUEST TIMEOUT
        // ==================================================

        if (error.code === 'ECONNABORTED') {

          toast.error(
            'Request timeout. Please try again.'
          );

          const customError: ApiError = {
            handled: true,
            message:
              'Request timeout.',
          };

          return Promise.reject(customError);
        }

        // ==================================================
        // STATUS CODE
        // ==================================================

        const status = error.response.status;

        // ==================================================
        // 400 BAD REQUEST
        // ==================================================

        if (status === 400) {

          const message =
            (error.response.data as any)?.message ||
            'Invalid request.';

          toast.error(message);

          return Promise.reject({
            handled: true,
            message,
            status,
          });
        }

        // ==================================================
        // 401 UNAUTHORIZED
        // ==================================================

        if (status === 401) {

          const currentPath =
            window.location.pathname;

          // Avoid login redirect loop
          if (
            currentPath !== '/login' &&
            !this.isRedirectingToLogin
          ) {

            const token =
              localStorage.getItem('token');

            // Only logout if token existed
            if (token) {

              this.isRedirectingToLogin = true;

              // Clear auth
              localStorage.removeItem('token');
              localStorage.removeItem('user');

              // Prevent duplicate toasts
              if (!this.sessionToastShown) {

                this.sessionToastShown = true;

                toast.error(
                  'Session expired. Please login again.'
                );

                setTimeout(() => {
                  this.sessionToastShown = false;
                }, 4000);
              }

              // SOFT REDIRECT
              setTimeout(() => {

                window.location.replace('/login');

              }, 1200);
            }
          }

          return Promise.reject({
            handled: true,
            message: 'Unauthorized',
            status,
          });
        }

        // ==================================================
        // 403 FORBIDDEN
        // ==================================================

        if (status === 403) {

          toast.error(
            'You do not have permission to perform this action.'
          );

          return Promise.reject({
            handled: true,
            message:
              'Forbidden access.',
            status,
          });
        }

        // ==================================================
        // 404 NOT FOUND
        // ==================================================

        if (status === 404) {

          return Promise.reject({
            handled: true,
            message:
              'Requested resource not found.',
            status,
          });
        }

        // ==================================================
        // 422 VALIDATION ERROR
        // ==================================================

        if (status === 422) {

          const message =
            (error.response.data as any)?.message ||
            'Validation failed.';

          toast.error(message);

          return Promise.reject({
            handled: true,
            message,
            status,
          });
        }

        // ==================================================
        // 500+ SERVER ERROR
        // ==================================================

        if (status >= 500) {

          toast.error(
            'Server error occurred. Please try again later.'
          );

          return Promise.reject({
            handled: true,
            message:
              'Internal server error.',
            status,
          });
        }

        // ==================================================
        // FALLBACK ERROR
        // ==================================================

        const fallbackMessage =
          (error.response.data as any)?.message ||
          error.message ||
          'Something went wrong';

        return Promise.reject({
          handled: true,
          message: fallbackMessage,
          status,
        });
      }
    );
  }

  // ======================================================
  // HTTP METHODS
  // ======================================================

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {

    return this.client.get<T>(
      url,
      config
    );
  }

  public async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {

    return this.client.post<T>(
      url,
      data,
      config
    );
  }

  public async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {

    return this.client.put<T>(
      url,
      data,
      config
    );
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {

    return this.client.delete<T>(
      url,
      config
    );
  }
}

// ======================================================
// EXPORT
// ======================================================

export const apiClient = new ApiClient();